
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DataTable, { TableColumn } from "react-data-table-component";
import "react-toastify/dist/ReactToastify.css";
import "../../style/customcssfile.css";
import "../../style/designer-milling-responsive.css";
import { Modal } from "react-bootstrap";
import { apiAxios } from "../../utils/apiAxios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface Machine {
  machineID: number;
  companyName: string;
  model: string;
  nestingSoftware: string;
  lithiumDisilcateCapacity: string;
  titaniumPremillsCapacity: string;
  createdDate: string;
  isActive?: boolean;
  isOnline: boolean;
}


const AddMachine: React.FC = () => {
  const [machine, setMachine] = useState({ companyName: "", model: "", nestingSoftware: "", lithiumDisilcateCapacity: "", titaniumPremillsCapacity: "", });
  const [machines, setMachines] = useState<Machine[]>([]);
  const [filteredMachines, setFilteredMachines] = useState<Machine[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const token = sessionStorage.getItem("token") || "";


  const fetchMachines = async () => {
    try {
      setLoading(true);
      const headers = {
        "Content-Type": "application/json", ...(token ? { AuthorizationData: token } : {}),
      };
      const res = await apiAxios.get(`${API_BASE}/MillingHub/All`, { headers });
      if (res.data?.isSuccess && res.data.data) {
        const sorted = res.data.data.sort(
          (a: Machine, b: Machine) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
        );
        setMachines(sorted);
        setFilteredMachines(sorted);
      } else {
        setMachines([]);
        setFilteredMachines([]);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.Status || "Failed to fetch machines");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMachines();
  }, []);


  useEffect(() => {
    const filtered = machines.filter(
      (m) =>
        m.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.nestingSoftware.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredMachines(filtered);
  }, [searchTerm, machines]);

useEffect(() => {
  if (showDeleteConfirm) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }

  return () => {
    document.body.style.overflow = "";
  };
}, [showDeleteConfirm]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setMachine({ ...machine, [name]: value });
  };


  const handleNumericChange = (
  e: React.ChangeEvent<HTMLInputElement>
) => {
  const { name, value } = e.target;
  const numericValue = value.replace(/[^0-9]/g, "");
  setMachine({ ...machine, [name]: numericValue });
};

  const saveMachine = async () => {
    if (!machine.companyName || !machine.model) {
      toast.error("Please fill required fields");
      return;
    }

    const token = sessionStorage.getItem("token");

    const headers = {
      "Content-Type": "application/json", ...(token ? { AuthorizationData: token } : {}),
    };

    try {
      if (editMode && editId) {
        await apiAxios.put(
          `${API_BASE}/MillingHub/Update/${editId}`, machine, { headers });
        toast.success("Machine updated successfully");
      } else {
        await apiAxios.post(`${API_BASE}/MillingHub/Add`, machine, { headers });
        toast.success("Machine added successfully");
      }
      resetForm();
      fetchMachines();
    } catch (err: any) {
      toast.error(err.response?.data?.Status || "API error");
    }
  };
  const resetForm = () => {
    setMachine({
      companyName: "",
      model: "",
      nestingSoftware: "",
      lithiumDisilcateCapacity: "",
      titaniumPremillsCapacity: "",
    });
    setEditMode(false);
    setEditId(null);
  };
  const handleEdit = (row: Machine) => {
    setMachine({
      companyName: row.companyName,
      model: row.model,
      nestingSoftware: row.nestingSoftware,
      lithiumDisilcateCapacity: row.lithiumDisilcateCapacity,
      titaniumPremillsCapacity: row.titaniumPremillsCapacity,
    });
    setEditMode(true);
    setEditId(row.machineID);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };


const toggleMachineOnline = async (id: number, currentStatus: boolean) => {
  try {
    const res = await apiAxios.post(
      `${API_BASE}/MillingHub/MachineOnlineOfline/${id}`,
      {},
      { headers: { AuthorizationData: token } }
    );

    if (res.data?.isSuccess) {
      toast.success(
        currentStatus ? "Machine is Offline" : "Machine is Online"
      );
      fetchMachines();
    } else {
      toast.error(res.data?.status || "Status update failed");
    }

  } catch (err: any) {
    toast.error(
      err.response?.data?.Status ||
      err.response?.data?.status ||
      "Status update failed"
    );
  }
};



  const confirmDelete = async () => {
    if (!deleteId) return;

    try {
      await apiAxios.post(
        `${API_BASE}/MillingHub/Delete/${deleteId}`,
        {},
        { headers: { AuthorizationData: token } }
      );
      toast.success("Machine deleted successfully");
      fetchMachines();
    } catch {
      toast.error("Delete failed");
    } finally {
      setShowDeleteConfirm(false);
      setDeleteId(null);
    }
  };

  const columns: TableColumn<Machine>[] = [
    { name: "MachineID",  width:"130px", center:true, selector: (r) => r.machineID, sortable: true },
    { name: "Company Name", center:true, width:"170px", selector: (r) => r.companyName, sortable: true },
    { name: "Model Name", center:true, width:"170px", selector: (r) => r.model, sortable: true },
    { name: "Nesting Software", center:true, width:"160px", selector: (r) => r.nestingSoftware },
    { name: "Lithium Capacity", center:true, width:"180px", selector: (r) => r.lithiumDisilcateCapacity },
    { name: "Titanium Capacity", center:true, width:"170px", selector: (r) => r.titaniumPremillsCapacity },
    {
      name: "Status",
      center:true,
       width:"170px",
      cell: (row) => (
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            checked={row.isOnline}
            onChange={() =>
              toggleMachineOnline(row.machineID, row.isOnline)
            }
          />
          <label className="ms-2">
            {row.isOnline ? "Online" : "Offline"}
          </label>
        </div>
      ),
    },
    {
     
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <button
            className="action-btn edit"
            onClick={() => handleEdit(row)}
          >
            <i className="bi bi-pencil-square"></i>
          </button>
          <button
            className="action-btn delete"
            onClick={() => handleDeleteClick(row.machineID)}
          >
            <i className="bi bi-trash"></i>
          </button>
        </div>
      ),
    },
  ];
  return (
    
    <div className="mt-3">
         <h4 className="mb-4">Add Machine</h4>
      <form>
        <div className="premium-card p-3 ">
          <div className="row">
            <div className="col-md-4"><label className="form-label">
              Enter Company Name <span style={{ color: "red" }}>*</span>
            </label> 
              <input
                type="text"
                className="form-control"
                name="companyName"
                value={machine.companyName}
                onChange={handleChange}
              />

            </div>

            <div className="col-md-4">
              <label className="form-label">
                Enter Model Name <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                className="form-control"
                name="model"
                value={machine.model}
                onChange={handleChange}
              />

            </div>

            <div className="col-md-4">
              <label className="form-label">Enter Nesting Software</label>
              <input
                type="text"
                className="form-control"
                name="nestingSoftware"
                value={machine.nestingSoftware}
                onChange={handleChange}
              />

            </div>
          </div>

          <div className="row mt-3">
            <div className="col-md-4">
              <label className="form-label">
                Lithium Disilicate Capacity (numeric)
              </label>
              <input
                type="text"
  className="form-control"
  name="lithiumDisilcateCapacity"
  value={machine.lithiumDisilcateCapacity}
  onChange={handleNumericChange}
              />

            </div>

            <div className="col-md-4">
              <label className="form-label">
                Titanium Premills Capacity (numeric)
              </label>
              <input
                type="text"
                className="form-control"
                name="titaniumPremillsCapacity"
                value={machine.titaniumPremillsCapacity}
                   onChange={handleNumericChange}
              />

            </div>
          </div>

          <div className="d-flex gap-2 pb-2 mt-4">
            <button
              type="button"
              className="btn btn-primary"
              onClick={saveMachine}
            >
              {editMode ? "Update" : "Submit"}
            </button>

            <button type="button"
             className="btn btn-secondary"
             onClick={resetForm}>
              Cancel
            </button>
          </div>
        </div>
      </form>

      <div className="premium-card mt-4 p-4">
        <div className="row">
          <div className="col-md-12 col-12 col-xl-3 col-sm-12">
                <div className="input-group search-orders">
   <input
          className="form-control mb-3"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
          <button className="btn btn-primary add-machine-seach-btn">
        <i className="bi bi-search"></i>
      </button>
        </div>
          </div>
           <div className="col-md-12">
               <DataTable
          columns={columns}
          data={filteredMachines}
          progressPending={loading}
          pagination
          striped
          highlightOnHover
          responsive
           className="admin-delivery-table"
        />
          </div>
        </div>
     
     
      </div>



<Modal
  show={showDeleteConfirm}
  onHide={() => setShowDeleteConfirm(false)}

  backdrop="static"
  keyboard={false}
>
  <Modal.Body className="pt-3 text-center">
    <p>Are you sure you want to delete this machine?</p>
  </Modal.Body>

  <Modal.Footer className="justify-content-center">
    <button
      className="btn py-2 px-3 btn-primary"
      onClick={confirmDelete}
    >
      Yes
    </button>

    <button
      className="btn py-2 px-3 btn-secondary mx-1"
      onClick={() => setShowDeleteConfirm(false)}
    >
      No
    </button>
  </Modal.Footer>
</Modal>


    </div>
  );
};

export default AddMachine;
